import React, { useState } from 'react';
import { usePlan } from './PlanContext';
import { TYPE_META, SITABLE_TYPES, TABLE_DIMS_M, getTablePxDims, formatDistance } from './seatUtils';
import { Trash2, RotateCw, Grid, Maximize2, Ruler, Settings2 } from 'lucide-react';

const TABLE_TYPES = ['round','oval','rect','square','honor','cocktail'];
const DECOR_TYPES = ['dj','dance','stage','buffet','bar','cake','photo','welcome','kids','door'];

let nextOffX = 0, nextOffY = 0;

export default function ToolsPanel({ collapsed, onToggle }) {
  const {
    addTable, selectedId, resolvedTables, updateTable, deleteTable,
    planData, pxPerM,
    roomWm, roomHm, roomX0, roomY0,
    snapToGrid, setSnapToGrid,
    showDistances, setShowDistances,
    updateRoomConfig,
  } = usePlan();

  const [tab, setTab] = useState('tables'); // 'tables' | 'salle' | 'options'
  const selectedTable = resolvedTables.find(t => t.id === selectedId);
  const brandColor    = planData?.brand_color || '#D4AF37';

  function handleAdd(type) {
    const { w, h } = getTablePxDims(type, pxPerM);
    const dims = TABLE_DIMS_M[type] || {};
    // Position dans la salle (décalage auto pour ne pas superposer)
    const baseX = roomX0 + 30 + (nextOffX % 6) * (w + 20);
    const baseY = roomY0 + 30 + Math.floor(nextOffX / 6) * (h + 30);
    nextOffX++;
    if (nextOffX > 29) { nextOffX = 0; nextOffY = 0; }
    addTable({
      type,
      w, h,
      capacity: dims.defaultCap || 0,
      label: TYPE_META[type]?.label || type,
      x: baseX,
      y: baseY,
      rotation: 0,
    });
  }

  if (collapsed) {
    return (
      <button onClick={onToggle}
        style={{
          position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)',
          zIndex: 30, background: '#fff', border: '1px solid #E5E7EB',
          borderLeft: 'none', borderRadius: '0 8px 8px 0',
          padding: '12px 6px', cursor: 'pointer', color: '#374151',
          boxShadow: '2px 0 8px rgba(0,0,0,0.08)',
        }}>
        ▶
      </button>
    );
  }

  return (
    <div style={{
      width: 220, flexShrink: 0,
      background: '#FFFFFF', borderRight: '1px solid #E5E7EB',
      display: 'flex', flexDirection: 'column',
      fontFamily: "'Montserrat', sans-serif",
      overflowY: 'auto',
    }}>
      {/* Onglets */}
      <div style={{ display: 'flex', borderBottom: '1px solid #F3F4F6' }}>
        {[
          { id: 'tables', icon: '🪑', label: 'Tables' },
          { id: 'salle',  icon: '📐', label: 'Salle' },
          { id: 'options', icon: '⚙️', label: '' },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            title={t.label}
            style={{
              flex: 1, padding: '8px 2px', border: 'none', cursor: 'pointer',
              fontSize: 11, fontWeight: 600, fontFamily: "'Montserrat',sans-serif",
              background: tab === t.id ? `${brandColor}15` : 'transparent',
              color: tab === t.id ? '#C58B19' : '#9CA3AF',
              borderBottom: tab === t.id ? `2px solid ${brandColor}` : '2px solid transparent',
            }}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* ── ONG : TABLES ─────────────────────────────────────────────────── */}
      {tab === 'tables' && (
        <>
          <Section title="Tables & sièges">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              {TABLE_TYPES.map(type => (
                <TypeButton key={type} type={type} brandColor={brandColor}
                  pxPerM={pxPerM} onClick={() => handleAdd(type)} />
              ))}
            </div>
          </Section>
          <Section title="Éléments de décor">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              {DECOR_TYPES.map(type => (
                <TypeButton key={type} type={type} brandColor={brandColor}
                  pxPerM={pxPerM} onClick={() => handleAdd(type)} />
              ))}
            </div>
          </Section>
        </>
      )}

      {/* ── ONG : SALLE ──────────────────────────────────────────────────── */}
      {tab === 'salle' && (
        <Section title="Dimensions de la salle">
          <RoomConfig
            roomWm={roomWm} roomHm={roomHm} pxPerM={pxPerM}
            updateRoomConfig={updateRoomConfig}
          />
        </Section>
      )}

      {/* ── ONG : OPTIONS ────────────────────────────────────────────────── */}
      {tab === 'options' && (
        <Section title="Options">
          <label style={checkStyle}>
            <input type="checkbox" checked={snapToGrid}
              onChange={e => setSnapToGrid(e.target.checked)} />
            Accrochage grille (0.5m)
          </label>
          <label style={{ ...checkStyle, marginTop: 8 }}>
            <input type="checkbox" checked={showDistances}
              onChange={e => setShowDistances(e.target.checked)} />
            Afficher les distances
          </label>
          <p style={{ fontSize: 10, color: '#9CA3AF', marginTop: 10, lineHeight: 1.5 }}>
            Ctrl+Molette ou pincement pour zoomer.
            Glissez les tables pour les déplacer.
          </p>
        </Section>
      )}

      {/* ── Table sélectionnée ───────────────────────────────────────────── */}
      {selectedTable && (
        <div style={{ marginTop: 'auto', borderTop: '2px solid #F3F4F6', padding: 12 }}>
          <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: '0.08em', color: '#9CA3AF', marginBottom: 8 }}>
            ✎ {selectedTable.label}
          </p>
          <SelectedEditor table={selectedTable} updateTable={updateTable}
            deleteTable={deleteTable} brandColor={brandColor} pxPerM={pxPerM} />
        </div>
      )}
    </div>
  );
}

// ─── Sous-composants ──────────────────────────────────────────────────────────

function Section({ title, children }) {
  return (
    <div style={{ padding: '10px 12px', borderBottom: '1px solid #F9FAFB' }}>
      <p style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: '0.08em', color: '#9CA3AF', marginBottom: 8 }}>
        {title}
      </p>
      {children}
    </div>
  );
}

function TypeButton({ type, brandColor, pxPerM, onClick }) {
  const meta  = TYPE_META[type] || {};
  const dims  = TABLE_DIMS_M[type];
  const isDecor = meta.decor;
  return (
    <button onClick={onClick} style={{
      padding: '7px 4px', borderRadius: 8, fontSize: 10, fontWeight: 600,
      background: isDecor ? '#F9FAFB' : `${brandColor}10`,
      border: `1.5px solid ${isDecor ? '#E5E7EB' : brandColor + '40'}`,
      color: isDecor ? '#374151' : '#C58B19',
      cursor: 'pointer', textAlign: 'center',
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
    }}>
      <TypeShape type={type} size={26} brandColor={brandColor} />
      <span style={{ fontSize: 9 }}>{meta.label || type}</span>
      {dims && (
        <span style={{ fontSize: 8, color: '#9CA3AF', fontWeight: 400 }}>
          {dims.wm < 1 ? `${dims.wm*100}×${dims.hm*100}cm` : `${dims.wm}×${dims.hm}m`}
        </span>
      )}
    </button>
  );
}

function TypeShape({ type, size: s, brandColor }) {
  const gold = brandColor || '#D4AF37';
  if (type === 'round')    return <div style={{ width:s, height:s, borderRadius:'50%', border:`2px solid ${gold}`, background:`${gold}18` }} />;
  if (type === 'oval')     return <div style={{ width:s*1.3, height:s*0.75, borderRadius:'50%', border:`2px solid ${gold}`, background:`${gold}18` }} />;
  if (type === 'rect')     return <div style={{ width:s*1.6, height:s*0.55, borderRadius:3, border:`2px solid ${gold}`, background:`${gold}18` }} />;
  if (type === 'square')   return <div style={{ width:s, height:s, borderRadius:3, border:`2px solid ${gold}`, background:`${gold}18` }} />;
  if (type === 'honor')    return <div style={{ width:s*1.8, height:s*0.45, borderRadius:3, border:`2.5px solid ${gold}`, background:`${gold}22` }} />;
  if (type === 'cocktail') return <div style={{ width:s*0.65, height:s*0.65, borderRadius:'50%', border:`2px solid ${gold}`, background:`${gold}18` }} />;
  return <div style={{ width:s, height:s*0.65, borderRadius:4, background:'#F3F4F6', border:'1.5px solid #D1D5DB' }} />;
}

function RoomConfig({ roomWm, roomHm, pxPerM, updateRoomConfig }) {
  const [w, setW] = useState(roomWm);
  const [h, setH] = useState(roomHm);
  const [scale, setScale] = useState(pxPerM);

  function apply() {
    const wm    = Math.max(3, Math.min(100, parseFloat(w) || 15));
    const hm    = Math.max(3, Math.min(100, parseFloat(h) || 10));
    const ppm   = Math.max(20, Math.min(120, parseInt(scale) || 60));
    updateRoomConfig({ room_width_m: wm, room_height_m: hm, px_per_m: ppm });
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <FieldM label="Largeur" value={w} onChange={setW} unit="m" />
        <FieldM label="Profondeur" value={h} onChange={setH} unit="m" />
      </div>
      <div>
        <label style={{ fontSize: 10, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 3 }}>
          Échelle (px/m)
        </label>
        <select value={scale} onChange={e => setScale(e.target.value)} style={inputStyle}>
          <option value={40}>40 (grande salle)</option>
          <option value={50}>50</option>
          <option value={60}>60 (standard)</option>
          <option value={80}>80 (petite salle)</option>
          <option value={100}>100 (très précis)</option>
        </select>
      </div>

      <div style={{ background: '#F9FAFB', borderRadius: 8, padding: '8px 10px', fontSize: 10, color: '#374151' }}>
        <p style={{ marginBottom: 3, fontWeight: 600 }}>Résumé</p>
        <p>{w}m × {h}m = {Math.round(w * h)} m²</p>
        <p style={{ color: '#9CA3AF' }}>1 case = 1m (grille)
Snap actif tous les 0.5m</p>
      </div>

      <button onClick={apply} style={{
        background: '#D4AF37', color: '#fff', border: 'none',
        borderRadius: 8, padding: '8px 0', fontWeight: 700,
        fontSize: 11, cursor: 'pointer',
        fontFamily: "'Montserrat',sans-serif",
      }}>
        Appliquer
      </button>

      <p style={{ fontSize: 9, color: '#9CA3AF', lineHeight: 1.5 }}>
        Les tables déjà placées restent à leur position.
        Réduire la taille de la salle peut les déplacer hors champ.
      </p>
    </div>
  );
}

function FieldM({ label, value, onChange, unit }) {
  return (
    <label style={{ fontSize: 10, fontWeight: 600, color: '#374151', display: 'flex', flexDirection: 'column', gap: 3 }}>
      {label}
      <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <input type="number" min={1} max={200} step={0.5}
          value={value}
          onChange={e => onChange(e.target.value)}
          style={{ ...inputStyle, flex: 1 }}
        />
        <span style={{ fontSize: 9, color: '#9CA3AF' }}>{unit}</span>
      </div>
    </label>
  );
}

function SelectedEditor({ table, updateTable, deleteTable, brandColor, pxPerM }) {
  const dims = TABLE_DIMS_M[table.type];
  const wm   = dims ? dims.wm : (table.w / pxPerM).toFixed(2);
  const hm   = dims ? dims.hm : (table.h / pxPerM).toFixed(2);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {/* Dimensions réelles */}
      <div style={{
        background: '#F9FAFB', borderRadius: 6, padding: '6px 8px',
        fontSize: 10, color: '#374151',
      }}>
        📐 {wm}m × {hm}m
        {formatDistance && (
          <span style={{ color: '#9CA3AF' }}> · position {formatDistance(table.x - Math.round(pxPerM), pxPerM)},{formatDistance(table.y - Math.round(pxPerM), pxPerM)}</span>
        )}
      </div>

      <label style={{ fontSize: 10, fontWeight: 600, color: '#374151' }}>
        Nom
        <input defaultValue={table.label}
          onBlur={e => updateTable(table.id, { label: e.target.value })}
          style={inputStyle} />
      </label>

      {SITABLE_TYPES.has(table.type) && (
        <label style={{ fontSize: 10, fontWeight: 600, color: '#374151' }}>
          Couverts
          <input type="number" min={0} max={30}
            defaultValue={table.capacity}
            onBlur={e => updateTable(table.id, { capacity: parseInt(e.target.value) || 0 })}
            style={inputStyle} />
        </label>
      )}

      {/* Rotation */}
      <label style={{ fontSize: 10, fontWeight: 600, color: '#374151' }}>
        Rotation
        <div style={{ display: 'flex', gap: 4, marginTop: 3 }}>
          {[0, 45, 90, 135].map(r => (
            <button key={r} onClick={() => updateTable(table.id, { rotation: r })}
              style={{
                flex: 1, padding: '4px 0', borderRadius: 5, fontSize: 9,
                border: '1px solid #D1D5DB', cursor: 'pointer',
                background: table.rotation === r ? brandColor : '#F9FAFB',
                color: table.rotation === r ? '#fff' : '#374151',
              }}>
              {r}°
            </button>
          ))}
        </div>
      </label>

      <button onClick={() => deleteTable(table.id)}
        style={{
          background: '#FEE2E2', color: '#DC2626', border: 'none',
          borderRadius: 6, padding: '6px 0', fontSize: 10, fontWeight: 600,
          cursor: 'pointer', display: 'flex', alignItems: 'center',
          justifyContent: 'center', gap: 4,
          fontFamily: "'Montserrat',sans-serif",
        }}>
        <Trash2 size={11} /> Supprimer
      </button>
    </div>
  );
}

const inputStyle = {
  display: 'block', width: '100%', marginTop: 2,
  padding: '5px 7px', borderRadius: 6, fontSize: 10,
  border: '1px solid #D1D5DB', outline: 'none',
  fontFamily: "'Montserrat', sans-serif",
  boxSizing: 'border-box',
};

const checkStyle = {
  display: 'flex', alignItems: 'center', gap: 6,
  fontSize: 11, color: '#374151', cursor: 'pointer',
};
